<template>
  <main dir="rtl" class="flex-1 pb-8">
    <div class="mx-auto px-4 sm:px-6 lg:px-8">
      <!-- Header -->
     
      <div class="flex justify-between items-center mb-6">
        <h1 class="text-3xl font-bold text-primary">إدارة جلسات الدارسين</h1>
        <!--
        <button 
          @click="openAddModal" 
          :disabled="!hasAvailableStudents"
          class="px-4 py-2 bg-primary text-white rounded hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
          <i class="fas fa-plus ml-2"></i>
          إضافة جلسة جديدة +
        </button>
         -->
      </div>
   
      <!-- No Available Students Warning -->
      <div v-if="!hasAvailableStudents" class="mb-6 bg-yellow-100 border-2 border-yellow-400 text-yellow-700 px-6 py-4 rounded-lg relative text-right">
        <div class="flex justify-between items-center">
          <div>
            <strong class="font-bold ml-2">تنبيه!</strong>
            <span class="block sm:inline">جميع الدارسين لديهم جلسات بالفعل. لا يمكن إضافة جلسات جديدة في الوقت الحالي.</span>
          </div>
          <button
            @click="loadAvailableStudentOptions"
            class="px-3 py-1 bg-yellow-500 text-white rounded hover:bg-yellow-600 transition-colors text-sm">
            <i class="fas fa-sync-alt ml-1"></i>
            تحديث القائمة
          </button>
        </div>
      </div>

      <!-- Search and Filters -->
      <div class="mb-6 bg-white p-4 rounded-lg shadow">
        <div class="flex gap-4 items-center">
          <div class="flex-1">
            <input
              v-model="searchQuery"
              @input="handleSearch"
              type="text"
              placeholder="البحث بالاسم أو اسم المعلم..."
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary text-right"
            />
          </div>
          <div class="flex items-center gap-2">
            <label class="text-sm text-gray-700">التاريخ:</label>
            <input
              v-model="selectedDate"
              type="date"
              @change="handleSearch"
              class="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <button
              v-if="selectedDate"
              @click="clearDateFilter"
              type="button"
              class="px-2 py-1 text-sm text-gray-600 hover:text-gray-800"
              title="مسح التاريخ">
              <i class="fas fa-times"></i>
            </button>
          </div>
          <div class="flex items-center gap-2">
            <label class="text-sm text-gray-700">عدد السجلات:</label>
            <select
              v-model="pageSize"
              @change="handlePageSizeChange"
              class="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary">
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
              <option value="0">الكل</option>
            </select>
          </div>
          <button
            @click="loadStudentSessions"
            class="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors">
            <i class="fas fa-sync-alt ml-2"></i>
            تحديث
          </button>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="text-center py-8">
        <div class="inline-block animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
        <p class="mt-4 text-lg text-primary">جاري التحميل...</p>
      </div>

      <!-- Error State -->
      <div v-if="error" class="bg-red-100 border-2 border-red-400 text-red-700 px-6 py-4 rounded-lg relative text-right mb-4">
        <strong class="font-bold ml-2">خطأ!</strong>
        <span class="block sm:inline">{{ error }}</span>
      </div>

      <!-- Success Message -->
      <div v-if="successMessage" class="bg-green-100 border-2 border-green-400 text-green-700 px-6 py-4 rounded-lg relative text-right mb-4">
        <strong class="font-bold ml-2">نجح!</strong>
        <span class="block sm:inline">{{ successMessage }}</span>
      </div>

      <!-- Student Sessions Table -->
      <div v-if="!loading" class="bg-white rounded-lg shadow overflow-hidden">
        <div class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-secondary text-white">
              <tr>
                <th class="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider">الطالب</th>
                <th class="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider">المعلم</th>
                <th class="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider">أيام الجلسة</th>
                <th class="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider">وقت الجلسة</th>
                <th class="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider">الصلاة</th>
                <th class="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider">تاريخ البداية</th>
                <th class="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider">تاريخ النهاية</th>
                <th class="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider">الحالة</th>
                <!--
                <th class="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider">الإجراءات</th>
                -->
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr v-for="session in studentSessions" :key="session.id" class="hover:bg-gray-50">
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="text-sm font-medium text-gray-900">{{ session.studentName }}</div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="text-sm text-gray-900">{{ session.teacherName }}</div>
                </td>
                <td class="px-6 py-4">
                  <div class="text-sm text-gray-900" v-if="session.scheduleDayNames && session.scheduleDayNames.length > 0">
                    <span v-for="(dayName, index) in session.scheduleDayNames" :key="index">
                      {{ dayName }}<span v-if="index < session.scheduleDayNames.length - 1">، </span>
                    </span>
                  </div>
                  <div class="text-sm text-gray-500" v-else>غير محدد</div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="text-sm text-gray-900" v-if="session.sessionStartTime">
                    {{ formatTime(session.sessionStartTime) }} - {{ formatTime(session.sessionEndTime) }}
                  </div>
                  <div class="text-sm text-gray-500" v-else>غير محدد</div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <span class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full"
                        :class="getPrayerBadgeClass(session.prayerName)">
                    {{ getPrayerDisplayName(session.prayerName) }}
                  </span>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {{ formatDate(session.startDate) }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {{ formatDate(session.endDate) }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <span class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full"
                        :class="session.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'">
                    {{ session.isActive ? 'نشط' : 'غير نشط' }}
                  </span>
                </td>
                <!--
                <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div class="flex gap-2">
                    <button @click="openEditModal(session)" 
                            class="text-blue-600 hover:text-blue-900 transition-colors">
                      <i class="fas fa-edit"></i>
                    </button>
                    <button @click="openDeleteModal(session)" 
                            class="text-red-600 hover:text-red-900 transition-colors">
                      <i class="fas fa-trash"></i>
                    </button>
                  </div>
                </td>
                -->
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        <div class="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
          <div class="flex-1 flex justify-between sm:hidden">
            <button
              @click="prevPage"
              :disabled="currentPage === 0"
              class="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">
              السابق
            </button>
            <button
              @click="nextPage"
              :disabled="currentPage >= totalPages - 1"
              class="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">
              التالي
            </button>
          </div>
          <div v-if="pageSize !== 0" class="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p class="text-sm text-gray-700">
                عرض
                <span class="font-medium">{{ (currentPage * pageSize) + 1 }}</span>
                إلى
                <span class="font-medium">{{ Math.min((currentPage + 1) * pageSize, totalElements) }}</span>
                من
                <span class="font-medium">{{ totalElements }}</span>
                جلسة
              </p>
            </div>
            <div>
              <nav class="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                <button
                  @click="prevPage"
                  :disabled="currentPage === 0"
                  class="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">
                  <span class="sr-only">السابق</span>
                  <i class="fas fa-chevron-right h-5 w-5"></i>
                </button>
                <button
                  v-for="page in visiblePages"
                  :key="page"
                  @click="goToPage(page)"
                  :class="[
                    'relative inline-flex items-center px-4 py-2 border text-sm font-medium',
                    page === currentPage
                      ? 'z-10 bg-primary border-primary text-white'
                      : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                  ]">
                  {{ page + 1 }}
                </button>
                <button
                  @click="nextPage"
                  :disabled="currentPage >= totalPages - 1"
                  class="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">
                  <span class="sr-only">التالي</span>
                  <i class="fas fa-chevron-left h-5 w-5"></i>
                </button>
              </nav>
            </div>
          </div>
        </div>
      </div>

      <!-- Add/Edit Modal -->
      <div v-if="showModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div class="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
          <h3 class="text-xl font-bold mb-4 text-primary">
            {{ isEditMode ? 'تعديل جلسة الطالب' : 'إضافة جلسة جديدة' }}
          </h3>
          
          <form @submit.prevent="saveSession">
            <!-- Student Selection -->
            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700 mb-2">الطالب *</label>
              <select
                v-model="form.studentId"
                required
                :disabled="!hasAvailableStudents"
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary text-right disabled:opacity-50 disabled:cursor-not-allowed">
                <option value="" v-if="!hasAvailableStudents">لا يوجد دارسين متاحين</option>
                <option value="" v-else>اختر الطالب</option>
                <option v-for="student in studentOptions" :key="student.id" :value="student.id">
                  {{ student.fullName }}
                </option>
              </select>
              <!-- Debug info -->
              <div class="mt-1 text-xs text-gray-500">
                عدد الطلاب المتاحين: {{ studentOptions.length }}
                <span v-if="studentOptions.length > 0">
                  ({{ studentOptions.slice(0, 3).map(st => st.fullName).join(', ') }}{{ studentOptions.length > 3 ? '...' : '' }})
                </span>
              </div>
              <p v-if="!hasAvailableStudents" class="mt-1 text-sm text-yellow-600">
                جميع الدارسين لديهم جلسات بالفعل
              </p>
            </div>

            <!-- Teacher Session Selection -->
            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700 mb-2">جلسة المعلم *</label>
              <select
                v-model="form.sessionId"
                @change="loadSessionTimes"
                required
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary text-right">
                <option value="">اختر جلسة المعلم</option>
                <option v-for="session in teacherSessionOptions" :key="session.id" :value="session.id">
                  {{ session.sessionsName }} - {{ session.teacherName }}
                </option>
              </select>
              <!-- Debug info -->
              <div class="mt-1 text-xs text-gray-500">
                عدد جلسات المعلمين: {{ teacherSessionOptions.length }}
                <span v-if="teacherSessionOptions.length > 0">
                  ({{ teacherSessionOptions.slice(0, 2).map(s => s.sessionsName).join(', ') }}{{ teacherSessionOptions.length > 2 ? '...' : '' }})
                </span>
              </div>
            </div>

            <!-- Session Time Selection -->
            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700 mb-2">وقت الجلسة (اختياري)</label>
              <select
                v-model="form.sessionTimeId"
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary text-right">
                <option value="">بدون وقت محدد</option>
                <option v-for="sessionTime in sessionTimeOptions" :key="sessionTime.id" :value="sessionTime.id">
                  {{ sessionTime.prayerNameAr }} - {{ sessionTime.startTimeArabic }} إلى {{ sessionTime.endTimeArabic }}
                </option>
              </select>
              <!-- Debug info -->
              <div class="mt-1 text-xs text-gray-500">
                عدد الأوقات المتاحة: {{ sessionTimeOptions.length }}
                <span v-if="sessionTimeOptions.length > 0">
                  ({{ sessionTimeOptions.map(st => st.prayerNameAr).join(', ') }})
                </span>
              </div>
            </div>

            <!-- Start Date -->
            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700 mb-2">تاريخ البداية *</label>
              <input
                v-model="form.startDate"
                type="date"
                required
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary text-right">
            </div>

            <!-- End Date -->
            <div class="mb-6">
              <label class="block text-sm font-medium text-gray-700 mb-2">تاريخ النهاية *</label>
              <input
                v-model="form.endDate"
                type="date"
                required
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary text-right">
            </div>

            <!-- Form Actions -->
            <div class="flex justify-end gap-3">
              <button
                type="button"
                @click="closeModal"
                class="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 transition-colors">
                إلغاء
              </button>
              <button
                type="submit"
                :disabled="submitting"
                class="px-4 py-2 bg-primary text-white rounded hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                <span v-if="submitting">جاري الحفظ...</span>
                <span v-else>{{ isEditMode ? 'تحديث' : 'إضافة' }}</span>
              </button>
            </div>
          </form>
        </div>
      </div>

      <!-- Delete Confirmation Modal -->
      <div v-if="showDeleteModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div class="bg-white rounded-lg p-6 w-96 max-w-md mx-4">
          <h3 class="text-xl font-bold mb-4 text-red-600">تأكيد الحذف</h3>
          <p class="mb-6 text-gray-700">
            هل أنت متأكد من حذف جلسة الطالب <strong>{{ deletingSession?.studentName }}</strong>؟
            <br>
            <span class="text-sm text-gray-600">ملاحظة: سيتم إلغاء تفعيل الجلسة وليس حذفها نهائياً</span>
          </p>
          <div class="flex justify-end gap-3">
            <button
              @click="closeDeleteModal"
              class="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 transition-colors">
              إلغاء
            </button>
            <button
              @click="confirmDelete"
              :disabled="submitting"
              class="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
              <span v-if="submitting">جاري الحذف...</span>
              <span v-else>حذف</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </main>
</template>

<script>
import axios from 'axios';
import url from '@/url';

export default {
  name: 'StudentSessionsManagement',
  data() {
    return {
      // Data
      studentSessions: [],
      studentOptions: [],
      teacherSessionOptions: [],
      sessionTimeOptions: [],
      
      // Pagination
      currentPage: 0,
      pageSize: 10,
      totalElements: 0,
      totalPages: 0,
      
      // Search
      searchQuery: '',
      searchTimeout: null,
      selectedDate: '',
      
      // UI State
      loading: false,
      submitting: false,
      error: null,
      successMessage: '',
      
      // Modals
      showModal: false,
      showDeleteModal: false,
      isEditMode: false,
      editingSession: null,
      deletingSession: null,
      
      // Form
      form: {
        studentId: '',
        sessionId: '',
        sessionTimeId: '',
        startDate: '',
        endDate: ''
      }
    };
  },
  computed: {
    visiblePages() {
      console.log('Computing visiblePages:', {
        currentPage: this.currentPage,
        totalPages: this.totalPages,
        pageSize: this.pageSize,
        totalElements: this.totalElements
      });
      
      const pages = [];
      
      // Always show at least the current page
      if (this.totalPages > 0) {
        const start = Math.max(0, this.currentPage - 2);
        const end = Math.min(this.totalPages - 1, this.currentPage + 2);
        
        for (let i = start; i <= end; i++) {
          pages.push(i);
        }
      }
      
      // If no pages calculated, at least show page 0
      if (pages.length === 0 && this.totalElements > 0) {
        pages.push(0);
      }
      
      console.log('Visible pages result:', { start: pages.length > 0 ? Math.min(...pages) : 0, end: pages.length > 0 ? Math.max(...pages) : 0, pages });
      return pages;
    },
    hasAvailableStudents() {
      const hasStudents = this.studentOptions.length > 0;
      console.log('hasAvailableStudents computed:', {
        studentOptionsLength: this.studentOptions.length,
        hasStudents: hasStudents,
        studentOptions: this.studentOptions
      });
      return hasStudents;
    }
  },
  async mounted() {
    console.log('StudentSessionsManagement component mounted');
    // Load initial data when component mounts
    await this.loadStudentSessions();
    await this.loadStudentOptions();
    await this.loadTeacherSessionOptions();
    await this.loadSessionTimes();
    console.log('Initial data loading completed');
  },
  methods: {
    // Data Loading
    async loadStudentSessions() {
      this.loading = true;
      this.error = null;
      
      try {
        const params = {
          page: this.pageSize === 0 ? 0 : this.currentPage, // If showing all, always use page 0
          size: this.pageSize === 0 ? 999999 : this.pageSize, // If showing all, use very large number
          search: this.searchQuery || undefined,
          date: this.selectedDate || undefined
        };

        // Use the main student sessions endpoint for all requests (it handles search and filtering)
        const searchUrl = url.student_sessions;

        const fullUrl = process.env.VUE_APP_SERVICE_URL + searchUrl;
        console.log('Calling API URL:', fullUrl);
        console.log('With params:', params);

        const response = await axios.get(fullUrl, {
          params: params,
          headers: {
            'X-Authorization': localStorage.getItem('token')
          }
        });
        
        if (response.data?.data?.content) {
          // New pagination structure from API
          console.log('API Response Data:', response.data);
          console.log('Student Sessions Data:', response.data.data);
          this.studentSessions = response.data.data.content || [];
          this.totalElements = response.data.data.totalElements || 0;
          this.totalPages = response.data.data.totalPages || 0;
          console.log('Processed Data:', {
            sessions: this.studentSessions,
            totalElements: this.totalElements,
            totalPages: this.totalPages,
            currentPage: this.currentPage,
            pageSize: this.pageSize
          });
        } else if (response.data?.data && Array.isArray(response.data.data)) {
          // Direct data structure from API (fallback)
          console.log('API Response Data (fallback):', response.data);
          this.studentSessions = response.data.data || [];
          this.totalElements = response.data.data.length || 0;
          this.totalPages = Math.ceil(this.totalElements / this.pageSize) || 0;
          console.log('Fallback Processed Data:', {
            sessions: this.studentSessions,
            totalElements: this.totalElements,
            totalPages: this.totalPages,
            currentPage: this.currentPage,
            pageSize: this.pageSize
          });
        } else if (response.data?.result?.data) {
          // Legacy nested structure if it exists
          const data = response.data.result.data;
          this.studentSessions = data.content || [];
          this.totalElements = data.totalElements || 0;
          this.totalPages = data.totalPages || 0;
        }
      } catch (err) {
        this.error = err.response?.data?.mesAr || 'خطأ في تحميل جلسات الدارسين';
        console.error('Error loading student sessions:', err);
      } finally {
        this.loading = false;
      }
    },
    
    async loadStudentOptions() {
      try {
        const response = await axios.get(process.env.VUE_APP_SERVICE_URL + url.student_sessions_options_students, {
          headers: {
            'X-Authorization': localStorage.getItem('token')
          }
        });
        
        console.log('Student Options Response:', response.data);
        
        // Handle both response structures
        if (response.data?.data) {
          this.studentOptions = response.data.data;
        } else if (response.data?.result?.data) {
          this.studentOptions = response.data.result.data;
        } else {
          console.warn('Unexpected response structure for student options:', response.data);
          this.studentOptions = [];
        }
        
        console.log('Loaded student options:', this.studentOptions);
      } catch (err) {
        console.error('Error loading student options:', err);
        this.studentOptions = [];
      }
    },
    
    async loadAvailableStudentOptions() {
      try {
        const response = await axios.get(process.env.VUE_APP_SERVICE_URL + url.student_sessions_options_available_students, {
          headers: {
            'X-Authorization': localStorage.getItem('token')
          }
        });
        
        console.log('Available Student Options Response:', response.data);
        
        // Handle both response structures
        if (response.data?.data) {
          this.studentOptions = response.data.data;
        } else if (response.data?.result?.data) {
          this.studentOptions = response.data.result.data;
        } else {
          console.warn('Unexpected response structure for available student options:', response.data);
          this.studentOptions = [];
        }
        
        // Show message if no available students
        if (this.studentOptions.length === 0) {
          this.successMessage = 'تم تحديث قائمة الدارسين المتاحين. جميع الدارسين لديهم جلسات بالفعل.';
          setTimeout(() => {
            this.successMessage = '';
          }, 5000);
        }
        
        console.log('Loaded available student options:', this.studentOptions);
      } catch (err) {
        console.error('Error loading available student options:', err);
        this.error = 'خطأ في تحميل قائمة الدارسين المتاحين';
        this.studentOptions = [];
      }
    },
    
    async loadTeacherSessionOptions() {
      try {
        const response = await axios.get(process.env.VUE_APP_SERVICE_URL + url.student_sessions_options_teacher_sessions, {
          headers: {
            'X-Authorization': localStorage.getItem('token')
          }
        });
        
        console.log('Teacher Session Options Response:', response.data);
        
        // Handle both response structures
        if (response.data?.data) {
          this.teacherSessionOptions = response.data.data;
        } else if (response.data?.result?.data) {
          this.teacherSessionOptions = response.data.result.data;
        } else {
          console.warn('Unexpected response structure for teacher session options:', response.data);
          this.teacherSessionOptions = [];
        }
        
        console.log('Loaded teacher session options:', this.teacherSessionOptions);
      } catch (err) {
        console.error('Error loading teacher session options:', err);
        this.teacherSessionOptions = [];
      }
    },
    
    async loadSessionTimes() {
      if (!this.form.sessionId) {
        console.log('No session ID selected, clearing session time options');
        this.sessionTimeOptions = [];
        return;
      }
      
      try {
        console.log('Loading session times for session ID:', this.form.sessionId);
        const fullUrl = process.env.VUE_APP_SERVICE_URL + url.student_sessions_options_session_times + this.form.sessionId;
        console.log('Full URL for session times:', fullUrl);
        
        const response = await axios.get(fullUrl, {
          headers: {
            'X-Authorization': localStorage.getItem('token')
          }
        });
        
        console.log('Session Times Response:', response.data);
        console.log('Response status:', response.status);
        
        // Handle both response structures
        if (response.data?.data) {
          this.sessionTimeOptions = response.data.data;
          console.log('Using response.data.data structure');
        } else if (response.data?.result?.data) {
          this.sessionTimeOptions = response.data.result.data;
          console.log('Using response.data.result.data structure');
        } else {
          console.warn('Unexpected response structure for session times:', response.data);
          this.sessionTimeOptions = [];
        }
        
        console.log('Final session time options:', this.sessionTimeOptions);
        console.log('Number of session time options:', this.sessionTimeOptions.length);
        
        // Debug: Check if Arabic times are present
        if (this.sessionTimeOptions.length > 0) {
          console.log('First session time option details:', this.sessionTimeOptions[0]);
          console.log('startTimeArabic:', this.sessionTimeOptions[0].startTimeArabic);
          console.log('endTimeArabic:', this.sessionTimeOptions[0].endTimeArabic);
        }
      } catch (err) {
        console.error('Error loading session times:', err);
        console.error('Error details:', {
          message: err.message,
          response: err.response?.data,
          status: err.response?.status
        });
        this.sessionTimeOptions = [];
      }
    },
    
    // Search
    handleSearch() {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }

      this.searchTimeout = setTimeout(() => {
        this.currentPage = 0;
        this.loadStudentSessions();
      }, 500);
    },

    clearDateFilter() {
      this.selectedDate = '';
      this.currentPage = 0;
      this.loadStudentSessions();
    },
    
    // Page Size Change
    handlePageSizeChange() {
      this.currentPage = 0; // Reset to first page
      this.loadStudentSessions();
    },
    
    // Pagination
    prevPage() {
      if (this.currentPage > 0) {
        this.currentPage--;
        this.loadStudentSessions();
      }
    },
    
    nextPage() {
      if (this.currentPage < this.totalPages - 1) {
        this.currentPage++;
        this.loadStudentSessions();
      }
    },
    
    goToPage(page) {
      this.currentPage = page;
      this.loadStudentSessions();
    },
    
    // Modal Management
    async openAddModal() {
      console.log('Opening Add Modal...');
      this.isEditMode = false;
      this.editingSession = null;
      this.resetForm();
      
      console.log('Loading available student options...');
      await this.loadAvailableStudentOptions();
      console.log('Student options after loading:', this.studentOptions);
      
      console.log('Loading teacher session options...');
      await this.loadTeacherSessionOptions();
      console.log('Teacher session options after loading:', this.teacherSessionOptions);
      
      // Load session times if a teacher session is already selected
      if (this.form.sessionId) {
        console.log('Loading session times for pre-selected session...');
        await this.loadSessionTimes();
      }
      
      console.log('Modal state before showing:', {
        hasAvailableStudents: this.hasAvailableStudents,
        studentOptionsLength: this.studentOptions.length,
        teacherSessionOptionsLength: this.teacherSessionOptions.length,
        sessionTimeOptionsLength: this.sessionTimeOptions.length
      });
      
      this.showModal = true;
    },
    
    async openEditModal(session) {
      this.isEditMode = true;
      this.editingSession = session;
      await this.loadStudentOptions();
      await this.loadTeacherSessionOptions();
      
      // Populate form
      this.form = {
        studentId: session.studentId,
        sessionId: session.sessionId,
        sessionTimeId: session.sessionTimeId || '',
        startDate: session.startDate,
        endDate: session.endDate
      };
      
      // Load session times for the selected session
      if (session.sessionId) {
        await this.loadSessionTimes();
      }
      
      this.showModal = true;
    },
    
    closeModal() {
      this.showModal = false;
      this.resetForm();
      this.clearMessages();
    },
    
    openDeleteModal(session) {
      this.deletingSession = session;
      this.showDeleteModal = true;
    },
    
    closeDeleteModal() {
      this.showDeleteModal = false;
      this.deletingSession = null;
    },
    
    resetForm() {
      this.form = {
        studentId: '',
        sessionId: '',
        sessionTimeId: '',
        startDate: '',
        endDate: ''
      };
      this.sessionTimeOptions = [];
    },
    
    // CRUD Operations
    async saveSession() {
      this.submitting = true;
      this.error = null;
      
      try {
        const payload = {
          studentId: parseInt(this.form.studentId),
          sessionId: parseInt(this.form.sessionId),
          sessionTimeId: this.form.sessionTimeId ? parseInt(this.form.sessionTimeId) : null,
          startDate: this.form.startDate,
          endDate: this.form.endDate
        };
        
        if (this.isEditMode) {
          await axios.put(
            process.env.VUE_APP_SERVICE_URL + url.student_sessions_by_id + this.editingSession.id,
            payload,
            {
              headers: {
                'X-Authorization': localStorage.getItem('token')
              }
            }
          );
          this.successMessage = 'تم تحديث جلسة الطالب بنجاح';
        } else {
          await axios.post(
            process.env.VUE_APP_SERVICE_URL + url.student_sessions,
            payload,
            {
              headers: {
                'X-Authorization': localStorage.getItem('token')
              }
            }
          );
          this.successMessage = 'تم إضافة جلسة الطالب بنجاح!';
        }
        
        this.closeModal();
        await this.loadStudentSessions();
        await this.loadAvailableStudentOptions(); // Refresh available students
        
        // Clear success message after 5 seconds
        setTimeout(() => {
          this.successMessage = '';
        }, 5000);
        
      } catch (err) {
        this.error = err.response?.data?.mesAr || 'خطأ في حفظ جلسة الطالب';
        console.error('Error saving session:', err);
      } finally {
        this.submitting = false;
      }
    },
    
    async confirmDelete() {
      this.submitting = true;
      this.error = null;
      
      try {
        await axios.delete(
          process.env.VUE_APP_SERVICE_URL + url.student_sessions_by_id + this.deletingSession.id,
          {
            headers: {
              'X-Authorization': localStorage.getItem('token')
            }
          }
        );
        
        this.successMessage = 'تم حذف جلسة الطالب بنجاح!';
        this.closeDeleteModal();
        await this.loadStudentSessions();
        await this.loadAvailableStudentOptions(); // Refresh available students
        
        // Clear success message after 5 seconds
        setTimeout(() => {
          this.successMessage = '';
        }, 5000);
        
      } catch (err) {
        this.error = err.response?.data?.mesAr || 'خطأ في حذف جلسة الطالب';
        console.error('Error deleting session:', err);
      } finally {
        this.submitting = false;
      }
    },
    
    // Utility Methods
    formatTime(time) {
      if (!time) return '';
      
      const [hours, minutes] = time.split(':');
      const date = new Date();
      date.setHours(parseInt(hours), parseInt(minutes), 0, 0);
      
      return date.toLocaleTimeString('ar-SA', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    },
    
    formatDate(date) {
      if (!date) return '';
      return new Date(date).toLocaleDateString('ar-SA');
    },
    
    getPrayerDisplayName(prayerName) {
      const prayerMap = {
        'FAJR': 'الفجر',
        'DHUHR': 'الظهر',
        'ASR': 'العصر',
        'MAGHRIB': 'المغرب',
        'ISHA': 'العشاء'
      };
      return prayerMap[prayerName] || prayerName || 'غير محدد';
    },
    
    getPrayerBadgeClass(prayerName) {
      const classMap = {
        'FAJR': 'bg-blue-100 text-blue-800',
        'DHUHR': 'bg-yellow-100 text-yellow-800',
        'ASR': 'bg-orange-100 text-orange-800',
        'MAGHRIB': 'bg-red-100 text-red-800',
        'ISHA': 'bg-purple-100 text-purple-800'
      };
      return classMap[prayerName] || 'bg-gray-100 text-gray-800';
    },
    
    clearMessages() {
      this.error = null;
      this.successMessage = '';
    }
  },
  
  async created() {
    await this.loadStudentSessions();
    await this.loadAvailableStudentOptions();
  }
};
</script>

<style scoped>
.text-primary {
  color: #c9a06c;
}

.bg-primary {
  background-color: #c9a06c;
}

.bg-primary-dark {
  background-color: #b8956a;
}

.border-primary {
  border-color: #c9a06c;
}

.bg-secondary {
  background-color: #d4a574;
}

.focus\:ring-primary:focus {
  --tw-ring-color: #c9a06c;
}

.hover\:bg-primary-dark:hover {
  background-color: #b8956a;
}
</style>
